import React, { useMemo, useState } from "react";

import LocationSearchField, { type LocationData } from "ui/LocationSearchField";
import { placeApi, type PlaceRecord } from "lib/api/places";

interface PlacePickerProps {
  value: PlaceRecord | null;
  onChange: (place: PlaceRecord | null) => void;
  placeholder?: string;
  className?: string;
}

const PlacePicker: React.FC<PlacePickerProps> = ({
  value,
  onChange,
  placeholder = "Search for any location",
  className = "",
}) => {
  const [error, setError] = useState<string | null>(null);

  const locationValue = useMemo<LocationData | null>(() => {
    if (!value) {
      return null;
    }

    const lat = typeof value.lat === "number" ? value.lat : 0;
    const lng = typeof value.lng === "number" ? value.lng : 0;
    return {
      formatted_address: value.formatted_address || value.name,
      city: value.locality || "",
      state: value.region || "",
      country: value.country_code || "",
      name: value.name,
      lat,
      lng,
      placeId: value.google_place_id || "",
    };
  }, [value]);

  const handleSelect = async (location: LocationData | null) => {
    if (!location) {
      setError(null);
      onChange(null);
      return;
    }

    try {
      const resolvedPlace = await placeApi.createFromLocationData(location);
      onChange(resolvedPlace);
      setError(null);
    } catch {
      setError("Unable to save that place.");
    }
  };

  return (
    <div className={className}>
      <LocationSearchField
        labelText="Place"
        fieldPlaceholder={placeholder}
        className="w-full"
        value={locationValue}
        onChange={(location) => {
          void handleSelect(location);
        }}
        onClear={() => {
          setError(null);
          onChange(null);
        }}
      />
      {error ? (
        <div className="mt-1 ml-3 text-xs text-warning-red dark:text-dark-warning-red">
          {error}
        </div>
      ) : null}
    </div>
  );
};

export default PlacePicker;
